import { ProviderSelection, SetupIntentResult } from '@fox/finance';
import { createContext, useContext } from 'react';
import { PaymentBlockMetadataType, TaxRatesData, ValidateDiscountResponse } from '../blocks/payment-block/contracts';
import { Plan } from '../blocks/subscription-block/contracts';
import { AccountInfoQuery } from '../blocks/transaction-block/contracts';

export type FormFinancialAPIHelpers = {
	fetchTaxRates: () => Promise<TaxRatesData | undefined | null>;
	fetchPlans: (input: { ids?: string | null; token?: string }) => Promise<Plan[]>;
	getProviders: (input: { provider?: PaymentBlockMetadataType['provider']; token?: string }) => Promise<ProviderSelection[]>;
	fetchCurrencies: (token?: string) => Promise<
		| {
				allowed: string[];
				default: string;
		  }
		| undefined
	>;
	setupIntent: (input: {
		formRequestId: string;
		provider: any;
		name: string;
		email: string;
		token?: string;
		ownerId?: string;
		ownerType?: string;
	}) => Promise<SetupIntentResult | undefined>;
	updateIntent: (input: {
		formRequestId: string;
		provider: ProviderSelection;
		amount: number;
		intentId: string;
		token?: string;
	}) => Promise<SetupIntentResult | undefined>;
	completeCardIntent: (input: {
		formRequestId: string;
		intentId: string;
		data: string;
		provider: ProviderSelection;
		token?: string;
	}) => Promise<{ success: boolean; methodId?: string; method?: string }>;
	completeDebit: (input: {
		formRequestId: string;
		intentId?: string;
		data?: string;
		accountName?: string;
		accountNumber?: string;
		bsb?: string;
		provider?: ProviderSelection;
		token?: string;
	}) => Promise<{ success: boolean; methodId?: string; method?: string }>;
	getAccountInfo: (input: { token?: string; ownerId?: string; ownerType?: string }) => Promise<AccountInfoQuery>;
	prorateFee: (input: { feeId: number; token?: string }) => Promise<number>;
	validateDiscount: (input: {
		discountToApply: string;
		ownerId: string;
		subscriptionSelections: string[];
		token?: string;
	}) => Promise<{ success: boolean; failReason?: string; discount?: ValidateDiscountResponse }>;
};

export const FormFinancialContext = createContext<FormFinancialAPIHelpers | null>(null);

export const useFormFinancialContext = () => {
	const context = useContext(FormFinancialContext);
	if (!context) {
		throw new Error('useFormFinancialContext must be used within a FormFinancialContextProvider');
	}
	return context;
};

FormFinancialContext.displayName = 'FormFinancialContext';
